import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Media from 'react-media';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import routes from '../../../utils/constants/routes';
import colors from '../../../styled/colors';
import { Button } from '../../../components/buttons';
import mediaQueries from '../../../utils/mediaQueries';
import {
  relaxInstagramImage,
  relaxInstagramImageWebp,
} from '../../../assets/images/imageRepository';
import {
  CircleLinesYellowIcon,
  CrossLinesIcon,
  HalfCircleIcon,
  WaveIcon,
} from '../iconsSVG/icons';

const Section = styled.section`
  background: linear-gradient(
    83.94deg,
    ${colors.orangeInstagram} -26.52%,
    ${colors.pinkInstagram} 54.11%,
    ${colors.blueInstagram} 144.72%
  );
  padding-top: 54px;
  padding-bottom: 8px;
  @media ${mediaQueries.mdUp} {
    background: radial-gradient(
      172.17% 574.34% at -36.43% 127.04%,
      ${colors.yellowInstagram} 0%,
      ${colors.orangeInstagram} 20.84%,
      ${colors.pinkInstagram} 68.07%,
      ${colors.blueInstagram}
    );
    display: flex;
    align-items: center;
    padding-top: 20px;
    padding-right: 40px;
  }
  @media ${mediaQueries.lgPlusUp} {
    padding-left: 26px;
    padding-bottom: 0;
    padding-top: 0;
  }
  @media ${mediaQueries.xlPlusUp} {
    background: linear-gradient(
      83.94deg,
      ${colors.orangeInstagram} -26.52%,
      ${colors.pinkInstagram} 54.11%,
      ${colors.blueInstagram} 144.71%
    );
    padding-left: 54px;
  }
`;
const CTAContainer = styled.div`
  padding-left: 22px;
  padding-right: 22px;
  position: relative;
  z-index: 1;
  @media ${mediaQueries.mdUp} {
    flex: 1;
    order: 2;
    padding-left: 0;
    padding-right: 0;
  }
  svg {
    position: absolute;
    z-index: -1;
  }
  .wave {
    height: 24px;
    width: 45px;
    top: -32px;
    left: 11px;
    @media ${mediaQueries.lgPlusUp} {
      width: 73px;
      height: 40px;
      top: -70px;
    }
  }
  .cross-lines {
    top: -30px;
    right: 18px;
  }
  .half-circle {
    bottom: -40px;
    left: calc(50% - 164px);
    @media ${mediaQueries.lgPlusUp} {
      bottom: 0;
      left: calc(50% - 234px);
    }
  }
  .circle-lines-yellow {
    bottom: -80px;
    right: calc(50% - 158px);
  }
`;
const CTAText = styled.p`
  color: ${colors.white};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  text-align: center;
  margin: 0;
  @media ${mediaQueries.lgPlusUp} {
    font-size: 25px;
    line-height: 31px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 32px;
    line-height: 44px;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;
const CustomButton = styled(Button)`
  && {
    background: ${colors.secondary};
    border-radius: 4px;
    box-shadow: none;
    color: ${colors.black3E};
    font-size: 16px;
    font-weight: 600;
    &:hover {
      background: ${colors.secondary};
      box-shadow: none;
      color: ${colors.black3E};
    }
    @media ${mediaQueries.lgPlusUp} {
      font-size: 20px;
    }
  }
`;
const ImageSection = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  bottom: -26px;
  @media ${mediaQueries.mdUp} {
    order: 1;
    bottom: 0;
  }
  @media ${mediaQueries.lgPlusUp} {
    bottom: -16px;
  }
  @media ${mediaQueries.xlPlusUp} {
    bottom: -28px;
  }
`;
const ImageContainer = styled.picture`
  display: block;
  max-width: 384px;
  @media ${mediaQueries.mdUp} {
    max-width: 416px;
    width: 416px;
    height: 271px;
  }
  @media ${mediaQueries.lgPlusUp} {
    max-width: 486px;
    width: 486px;
    height: 316px;
  }
  @media ${mediaQueries.xlPlusUp} {
    max-width: 729px;
    width: max-content;
    height: auto;
  }
`;
const Image = styled.img`
  width: 100%;
`;

const Relax = ({ daysTrial }) => {
  const history = useHistory();
  return (
    <Media queries={mediaQueries}>
      {(matches) => {
        let image = relaxInstagramImage.desktop;
        let imageWebp = relaxInstagramImageWebp.desktop;
        if (matches.xlPlusDownLess) {
          image = relaxInstagramImage.tabletPro;
          imageWebp = relaxInstagramImageWebp.tabletPro;
        }
        if (matches.lgPlusDownLess) {
          image = relaxInstagramImage.tablet;
          imageWebp = relaxInstagramImageWebp.tablet;
        }
        if (matches.mdDownLess) {
          image = relaxInstagramImage.mobile;
          imageWebp = relaxInstagramImageWebp.mobile;
        }
        return (
          <Section>
            <CTAContainer>
              <CTAText>
                <FormattedMessage
                  id="landing.instagram.relax.cta.text"
                  defaultMessage="All the content for your business on Instagram is fast and easy."
                />
              </CTAText>
              <ButtonContainer>
                <CustomButton onClick={() => history.push(routes.PRICING)}>
                  <FormattedMessage
                    id="landing.instagram.relax.cta.button"
                    defaultMessage="Start Your {daysTrial}-Day Trial"
                    values={{ daysTrial }}
                  />
                </CustomButton>
              </ButtonContainer>
              <WaveIcon className="wave" />
              <CrossLinesIcon className="cross-lines" />
              <HalfCircleIcon className="half-circle" />
              <CircleLinesYellowIcon className="circle-lines-yellow" />
            </CTAContainer>
            <ImageSection>
              <ImageContainer>
                <source
                  srcSet={imageWebp}
                  type="image/webp"
                />
                <source
                  srcSet={image}
                  type="image/png"
                />
                <Image src={image} alt="Guy relaxed" />
              </ImageContainer>
            </ImageSection>
          </Section>
        );
      }}
    </Media>
  );
};

Relax.propTypes = {
  daysTrial: PropTypes.number.isRequired,
};

export default Relax;
